import React from "react";
import _ from "underscore";
import { Alert, Button } from "reactstrap";

import { newToastEvent } from "../../utils/ToastUtil";
import AppPageHeader from "../Headers/AppPageHeader";
import BasicTableView from "../../views/supportal/CloudListView/BasicTableView";
import IconButton from "../../elements/IconButton";
import SectionsAndLayoutsDrawer from "../Drawers/SectionsAndLayoutsDrawer/SectionsAndLayoutsDrawer";
import { AppConfirm } from "../../utils/PopupUtil";
import { Field, LayoutAndSections, Module } from "../../types/TenantInterfaces";

export interface RecordSummarySection {
  id: string;
  Fields: {
    ColSpan: number;
    Field: string | Field;
  }[];
  Type: "RecordSummarySection";
  Title: string;
  Description?: string;
}

export interface StageProgressSection {
  id: string;
  Type: "StageProgress";
  Title: string;
  Description: string;
  ProgressField: string;
  Stages: { ColSpan: number; Stage: string }[];
  CompleteStage: boolean;
}

export interface RichTextSection {
  id: string;
  Field?: Field | string;
  Type: "RichTextSection";
  Title: string;
  Description: string;
}

interface Props {
  layouts: LayoutAndSections[];
  additionalSection: {};
  module: Module;
  onSave: ({
    moduleAPIName,
    layoutSections,
    onSuccess,
  }: {
    moduleAPIName: string;
    layoutSections: any;
    onSuccess: () => void;
  }) => void;
  submitting?: boolean;
}

interface State {
  layout: LayoutAndSections | null;
  layouts: Array<LayoutAndSections>;
  creatingLayout: boolean;
  additionalSection: {};
  addField: string;
  open: boolean;
  success: boolean;
  error: null | string;
}

class RecordDetailLayout extends React.Component<Props, State> {
  state = {
    edit: false,
    creatingLayout: false,
    layouts: Array<LayoutAndSections & { index: number }>(),
    layout: {} as LayoutAndSections,
    additionalSection: {},
    addField: "",
    open: false,
    success: false,
    error: null,
  };

  componentDidUpdate(prevProps: any) {
    if (prevProps.layouts !== this.props.layouts) {
      this.setState({ layouts: this.props.layouts });
    }

    if (prevProps.additionalSection !== this.props.additionalSection) {
      this.setState({ additionalSection: this.props.additionalSection });
    }
  }

  async removeLayout(layout: LayoutAndSections & { index: number }) {
    if (this.state.layouts?.length - 1 === 0) {
      newToastEvent(
        "warning",
        "You need to have at least one layout in the module."
      );
      return;
    }

    if (layout?.Default) {
      newToastEvent("warning", "You can't remove a default layout.");

      return;
    }

    const result = await AppConfirm({
      title: `Delete '${layout.Name}' Layout?`,
      html: `Are you sure you want to delete the '${layout.Name}' Layout?`,
    });

    if (!result.isConfirmed) {
      return;
    }

    let filteredLayouts = this.state.layouts.filter((l) => l.id !== layout.id);

    this.props.onSave({
      moduleAPIName: this.props.module.APIName,
      layoutSections: { action: "update", layout: filteredLayouts },
      onSuccess: () => {
        this.setState({ layouts: filteredLayouts });
        newToastEvent("success", "Successfully saved record detail layout");
      },
    });
  }

  setOrUnsetDefault(layout: LayoutAndSections) {
    const layoutIndex = _.findIndex(this.state.layouts, { id: layout.id });

    if (layoutIndex === -1 || !this.state.layouts[layoutIndex]) {
      newToastEvent("error", "Layout not found.");
      return;
    }

    const index = _.findIndex(this.state.layouts, { Default: true });

    if (index !== -1) {
      this.state.layouts[index].Default = false;
    }

    this.state.layouts[layoutIndex].Default = true;

    this.props.onSave({
      moduleAPIName: this.props.module.APIName,
      layoutSections: { action: "update", layout: this.state.layouts },
      onSuccess: () => {
        this.setState({ layouts: this.state.layouts });
        newToastEvent("success", "Successfully saved as default.");
      },
    });
  }

  componentDidMount() {
    if (this.props.layouts) {
      this.setState({ layouts: this.props.layouts });
    }

    if (this.props.additionalSection) {
      this.setState({ additionalSection: this.props.additionalSection });
    }
  }

  render() {
    return (
      <div>
        <div className="py-3 border-bottom border-lighter mb-3">
          <AppPageHeader
            size="sm"
            title={"Summary Sections & Layout"}
            description={`Customize how you'd like to present the fields for this data source.`}
            customContent={
              <Button
                size="full"
                color="primary"
                onClick={() => {
                  this.setState({ open: true, creatingLayout: true });
                }}
                style={{ width: 150 }}
              >
                Create Layout
              </Button>
            }
          />
        </div>
        <div>
          {this.state.success ? (
            <Alert className="mt-3 mb-0" color="success">
              {this.state.success}
            </Alert>
          ) : null}
          {this.state.error ? (
            <Alert className="mt-3 mb-0" color="danger">
              {this.state.error}
            </Alert>
          ) : null}
        </div>

        {/* Maps through all sections and depending on the section type renders a different component using sectionSwitch */}
        <BasicTableView
          loading={false}
          items={this.state.layouts ?? []}
          emptyMessage={
            this.state?.layouts?.length === 0
              ? "No items have been added."
              : "No items were found based on your search."
          }
          title="Layouts"
          getItemTitle={(l) => l.Name}
          getItemSubtitle={(l) => l.Description}
          CustomRenderer={(l) => {
            return (
              <div>
                <IconButton
                  icon={!!l?.Default ? "star" : "star-outline"}
                  onClick={() => {
                    this.setOrUnsetDefault(l);
                  }}
                  color="warning"
                />
                <IconButton
                  icon="pencil"
                  tooltipText="Edit Layout"
                  onClick={() => {
                    this.setState({ open: true, layout: l });
                  }}
                />
                <IconButton
                  icon="close"
                  tooltipText="Delete Layout"
                  color="danger"
                  onClick={() => {
                    this.removeLayout(l);
                  }}
                />
              </div>
            );
          }}
        />
        <SectionsAndLayoutsDrawer
          open={!!this.state.open}
          currentModule={this.props.module}
          layout={this.state.layout}
          submitting={this.props.submitting}
          onSave={(values: any) => {
            this.props.onSave(values);
            this.componentDidUpdate(this.state.layouts);
          }}
          onClose={() => {
            this.setState({
              open: false,
              layout: null,
              creatingLayout: false,
            });
          }}
          setLayoutState={(values: any) => {
            this.setState(values);
          }}
          createMode={this.state.creatingLayout}
        />
      </div>
    );
  }
}

export default RecordDetailLayout;
